import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { ApiError } from "@repo/api-client";
import {
  reactivateVenue,
  startImpersonation,
  suspendVenue,
  tenantHealth,
  type TenantHealth,
  type VenueHealth,
  type VenueState,
} from "./opsApi";
import { setActiveImpersonation } from "./impersonationStore";
import { EntitlementPanel } from "./EntitlementPanel";
import { opsButton } from "./opsStyles";
import { TrialPanel } from "./TrialPanel";

/**
 * The per-tenant record (super-admin.md, DOMAIN.md §C.13): the read-only health view plus the reasoned
 * overrides and the impersonation entry. Overrides require a MANDATORY reason (R2) and re-confirm on a
 * concurrent tenant edit (OP-4): a 409 carries the authoritative current version, which the re-confirm
 * button replays. Impersonation stores the returned session id and steps into the venue's staff shell.
 */
export function TenantRecordPage() {
  const { t } = useTranslation();
  const { tenantId = "" } = useParams();
  const navigate = useNavigate();
  const [health, setHealth] = useState<TenantHealth | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [reason, setReason] = useState("");
  const [staleVersion, setStaleVersion] = useState<{ venueId: string; version: number } | null>(
    null,
  );
  const [notice, setNotice] = useState<string | null>(null);

  // Reset to "loading" the moment tenantId changes — adjusted during render (comparing against the
  // last-seen tenantId) rather than via an effect, so the reset itself isn't a synchronous
  // setState-in-effect; the fetch below still needs a real effect (an external system).
  const [lastTenantId, setLastTenantId] = useState(tenantId);
  if (tenantId !== lastTenantId) {
    setLastTenantId(tenantId);
    setState("loading");
  }

  useEffect(() => {
    let active = true;
    tenantHealth(tenantId)
      .then((h) => active && (setHealth(h), setState("ready")))
      .catch(() => active && setState("error"));
    return () => {
      active = false;
    };
  }, [tenantId]);

  async function override(
    v: VenueHealth,
    action: "suspend" | "reactivate",
    expectedVersion: number,
  ) {
    if (!reason.trim()) {
      setNotice(t("ops.override.reasonRequired"));
      return;
    }
    setNotice(null);
    const fn = action === "suspend" ? suspendVenue : reactivateVenue;
    try {
      await fn(v.venueId, reason.trim(), expectedVersion);
      setStaleVersion(null);
      setReason("");
      setNotice(t("ops.override.applied"));
      setHealth(await tenantHealth(tenantId));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // OP-4: the tenant changed the value first — the 409 body IS the authoritative current record.
        const current = e.problem as unknown as VenueState | undefined;
        if (current && typeof current.version === "number") {
          setStaleVersion({ venueId: v.venueId, version: current.version });
          setNotice(t("ops.override.stale"));
          return;
        }
      }
      setNotice(t("ops.override.error"));
    }
  }

  async function impersonate(v: VenueHealth) {
    if (!reason.trim()) {
      setNotice(t("ops.override.reasonRequired"));
      return;
    }
    try {
      const session = await startImpersonation(v.venueId, reason.trim());
      setActiveImpersonation({
        sessionId: session.id,
        venueId: session.venueId,
        startedAt: session.startedAt,
      });
      navigate(`/v/${session.venueId}/overview`);
    } catch {
      setNotice(t("ops.impersonate.error"));
    }
  }

  if (state === "loading") return <p>{t("ops.record.loading")}</p>;
  if (state === "error" || !health)
    return <p className={css({ color: "fg.error" })}>{t("ops.record.error")}</p>;

  return (
    <section>
      <h1 className={css({ fontSize: "xl", fontWeight: "bold" })}>{health.accountName}</h1>
      <p className={css({ color: "fg.muted", mb: "4" })}>
        {t("ops.record.accountStatus")}: {health.accountStatus}
      </p>

      <label className={css({ display: "block", mb: "4" })}>
        <span className={css({ display: "block", fontWeight: "medium", mb: "1" })}>
          {t("ops.override.reason")}
        </span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          className={css({
            w: "full",
            p: "2",
            borderWidth: "1px",
            borderColor: "border.default",
            rounded: "md",
          })}
        />
      </label>
      {notice && (
        <output className={css({ display: "block", mb: "3", color: "fg.muted" })}>{notice}</output>
      )}

      <ul className={css({ display: "flex", flexDirection: "column", gap: "3" })}>
        {health.venues.map((v) => {
          const stale = staleVersion?.venueId === v.venueId ? staleVersion.version : null;
          const suspendable = v.lifecycle === "LIVE";
          return (
            <li
              key={v.venueId}
              className={css({
                p: "3",
                borderWidth: "1px",
                borderColor: "border.default",
                rounded: "md",
              })}
            >
              <div className={css({ display: "flex", justifyContent: "space-between", mb: "2" })}>
                <strong>{v.displayName}</strong>
                <span className={css({ color: "fg.muted", fontSize: "sm" })}>
                  {v.lifecycle} · v{stale ?? v.version}
                </span>
              </div>
              <dl
                className={css({
                  display: "flex",
                  gap: "4",
                  fontSize: "sm",
                  color: "fg.muted",
                  mb: "2",
                })}
              >
                <span>
                  {t("ops.health.bookings")}: {v.bookingsTotal}
                </span>
                <span>
                  {t("ops.health.openDsr")}: {v.openDsrCount}
                </span>
              </dl>
              <div className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
                {suspendable ? (
                  <button
                    type="button"
                    className={opsButton}
                    onClick={() => override(v, "suspend", stale ?? v.version)}
                  >
                    {stale !== null
                      ? t("ops.override.reconfirmSuspend")
                      : t("ops.override.suspend")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={opsButton}
                    onClick={() => override(v, "reactivate", stale ?? v.version)}
                  >
                    {stale !== null
                      ? t("ops.override.reconfirmReactivate")
                      : t("ops.override.reactivate")}
                  </button>
                )}
                <button type="button" className={opsButton} onClick={() => impersonate(v)}>
                  {t("ops.impersonate.start")}
                </button>
              </div>
              <EntitlementPanel venueId={v.venueId} />
            </li>
          );
        })}
      </ul>

      <TrialPanel tenantId={tenantId} />
    </section>
  );
}
